/**
 * Citation resolution utilities — shared by CLI and TUI.
 *
 * Parses citation data from SSE metadata (`MessageMetadataPayload`) and
 * resolves chunk references into displayable citation summaries and passages.
 *
 * Citation data flow:
 *   1. `tools.model_citations.tool_responses` maps citation number → CitationData
 *      (contains chunk_ids, statement, offsets)
 *   2. `tools.retrieval_chunk.tool_responses` and `tools.retrieval_full_context.tool_responses`
 *      map document file name → Chunk[] (contains content and metadata)
 *   3. This module joins those two data sets to produce resolved citations.
 */

import type { components } from '@arbidocs/client'

type MessageMetadataPayload = components['schemas']['MessageMetadataPayload']
type CitationData = components['schemas']['CitationData']
type Chunk = components['schemas']['Chunk']

// ── Public types ────────────────────────────────────────────────────────────

/** Re-export the underlying SDK ``Chunk`` type so consumers of
 *  ``ResolvedCitation`` don't have to depend on the generated
 *  ``@arbidocs/client`` schema directly. */
export type { Chunk }

/** A fully resolved citation with its source chunks. */
export interface ResolvedCitation {
  /** Citation number as a string (e.g. "1", "2") */
  citationNum: string
  /** Raw citation data from model_citations */
  citationData: CitationData
  /** Resolved chunks matching the citation's chunk_ids */
  chunks: Chunk[]
}

/** A compact citation summary for display in lists. */
export interface CitationSummary {
  citationNum: string
  statement: string
  docTitle: string
  pageNumber: number | null
  chunkCount: number
}

// ── Core functions ──────────────────────────────────────────────────────────

/**
 * Resolve all citations from SSE metadata into full citation objects.
 *
 * Reads `tools.model_citations.tool_responses` for citation→chunk_id mappings,
 * then builds a chunk lookup from `retrieval_chunk` and `retrieval_full_context`
 * tool responses to resolve each chunk_id to its full Chunk object.
 *
 * Returns citations sorted by citation number (ascending).
 */
export function resolveCitations(metadata: MessageMetadataPayload | null): ResolvedCitation[] {
  if (!metadata?.tools) return []

  const tools = metadata.tools as Record<string, unknown>

  // Extract model_citations tool_responses
  const modelCitations = tools.model_citations as
    { tool_responses?: Record<string, CitationData> } | undefined
  const citationMap = modelCitations?.tool_responses
  if (!citationMap || Object.keys(citationMap).length === 0) return []

  // Build chunk lookup from retrieval tools
  const chunkLookup = buildChunkLookup(tools)

  // Resolve each citation
  const resolved: ResolvedCitation[] = []
  for (const [citationNum, citationData] of Object.entries(citationMap)) {
    const chunks: Chunk[] = []
    for (const chunkId of citationData.chunk_ids ?? []) {
      const chunk = chunkLookup.get(chunkId)
      if (chunk) chunks.push(chunk)
    }
    resolved.push({ citationNum, citationData, chunks })
  }

  // Sort by citation number numerically
  resolved.sort((a, b) => Number(a.citationNum) - Number(b.citationNum))
  return resolved
}

/**
 * Produce compact summaries for display in citation lists.
 * Uses the first chunk of each citation for doc title and page number.
 */
export function summarizeCitations(resolved: ResolvedCitation[]): CitationSummary[] {
  return resolved.map((r) => {
    const firstChunk = r.chunks[0]
    return {
      citationNum: r.citationNum,
      statement: r.citationData.statement ?? '',
      docTitle: firstChunk?.metadata?.doc_title ?? 'Unknown document',
      pageNumber: firstChunk?.metadata?.page_number ?? null,
      chunkCount: r.chunks.length,
    }
  })
}

/**
 * Count the number of citations in metadata.
 * Returns 0 if metadata is null or has no citations.
 */
export function countCitations(metadata: MessageMetadataPayload | null): number {
  if (!metadata?.tools) return 0
  const tools = metadata.tools as Record<string, unknown>
  const modelCitations = tools.model_citations as
    { tool_responses?: Record<string, unknown> } | undefined
  const responses = modelCitations?.tool_responses
  return responses ? Object.keys(responses).length : 0
}

/**
 * Convert citation markdown to plain text with superscript-style numbers.
 * Replaces `[text](#cite-N)` with `text[N]`.
 */
export function stripCitationMarkdown(text: string): string {
  return text.replace(/\[([^\]]+)\]\(#cite-(\d+)\)/g, '$1[$2]')
}

// ── Internal helpers ────────────────────────────────────────────────────────

/**
 * Build a Map<chunk_ext_id, Chunk> from retrieval_chunk and retrieval_full_context
 * tool responses. Both tools store responses as { [docFileName]: Chunk[] }.
 */
function buildChunkLookup(tools: Record<string, unknown>): Map<string, Chunk> {
  const lookup = new Map<string, Chunk>()

  for (const toolName of ['retrieval_chunk', 'retrieval_full_context']) {
    const tool = tools[toolName] as { tool_responses?: Record<string, Chunk[]> } | undefined
    if (!tool?.tool_responses) continue

    for (const chunks of Object.values(tool.tool_responses)) {
      if (!Array.isArray(chunks)) continue
      for (const chunk of chunks) {
        const id = chunk.metadata?.chunk_ext_id
        if (id && !lookup.has(id)) {
          lookup.set(id, chunk)
        }
      }
    }
  }

  return lookup
}
